import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  InputAdornment,
  Avatar,
  CardHeader,
  LinearProgress
} from "@mui/material";
import {
  Add,
  Visibility,
  Search,
  FilterList,
  Refresh,
  Download,
  MoreVert,
  Business,
  LocalShipping,
  CheckCircle,
  Inventory,
  CalendarToday,
  TrendingUp,
  Receipt,
  Description,
  CloudDownload
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import {
  getReceiveHistory,
  getLinenVendors,
  getReceiveById
} from "../../services/api";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// Enhanced StatsCard component with larger fonts and better styling
const StatsCard = ({ title, value, subtitle, color = "primary", icon, ...props }) => (
  <Card 
    {...props}
    sx={{ 
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 48px rgba(0,0,0,0.15)'
      },
      ...props.sx
    }}
  >
    <CardContent sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography 
            color="text.secondary" 
            variant="h6" 
            sx={{ 
              mb: 2,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h2" 
            component="div" 
            fontWeight="bold" 
            color={`${color}.main`}
            sx={{ 
              fontSize: { xs: '2.5rem', md: '3rem' },
              mb: 1
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ 
                mt: 2,
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {icon && (
          <Box 
            sx={{ 
              color: `${color}.main`, 
              opacity: 0.9,
              '& .MuiSvgIcon-root': {
                fontSize: '3.5rem'
              }
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

export const LinenReceiveList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [receives, setReceives] = useState([]);
  const [filteredReceives, setFilteredReceives] = useState([]);
  const [vendors, setVendors] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(30, 'day'));
  const [dateTo, setDateTo] = useState(dayjs());
  const [periodFilter, setPeriodFilter] = useState("month");
  
  // Dialogs and menus
  const [selectedReceive, setSelectedReceive] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuReceive, setMenuReceive] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const periodOptions = [
    { value: "today", label: "📅 Today" },
    { value: "week", label: "📅 Last 7 Days" },
    { value: "month", label: "📅 Last 30 Days" },
    { value: "custom", label: "📅 Custom Range" }
  ];

  useEffect(() => {
    loadData();
  }, [periodFilter]);

  useEffect(() => {
    filterReceives();
  }, [receives, searchTerm, vendorFilter, dateFrom, dateTo]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      let dateRange = {};
      if (periodFilter !== "custom") {
        dateRange = { period: periodFilter };
      } else if (dateFrom && dateTo) {
        dateRange = {
          dateFrom: dateFrom.format('YYYY-MM-DD'),
          dateTo: dateTo.format('YYYY-MM-DD')
        };
      }

      const [receivesRes, vendorsRes] = await Promise.all([
        getReceiveHistory({ ...dateRange }),
        getLinenVendors({ status: "active" })
      ]);

      setReceives(receivesRes.data?.receives || []);
      setVendors(vendorsRes.data?.vendors || []);
      
    } catch (err) {
      console.error("Load data error:", err);
      enqueueSnackbar("❌ Failed to load receive history", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const filterReceives = () => {
    let filtered = [...receives];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(receive =>
        receive.dispatch?.dispatchNumber?.toLowerCase().includes(term) ||
        receive.vendor?.name?.toLowerCase().includes(term) ||
        receive.vendor?.vendorCode?.toLowerCase().includes(term) ||
        receive._id?.toLowerCase().includes(term)
      );
    }

    // Vendor filter
    if (vendorFilter !== "all") {
      filtered = filtered.filter(receive => receive.vendor?._id === vendorFilter);
    }

    // Date filter for custom range
    if (periodFilter === "custom" && dateFrom && dateTo) {
      filtered = filtered.filter(receive => {
        const receiveDate = dayjs(receive.received_at || receive.createdAt);
        return receiveDate.isAfter(dateFrom.subtract(1, 'day')) && 
               receiveDate.isBefore(dateTo.add(1, 'day'));
      });
    }

    // Sort by latest first
    filtered.sort((a, b) => new Date(b.received_at || b.createdAt) - new Date(a.received_at || a.createdAt));

    setFilteredReceives(filtered);
  };

  const handleMenuOpen = (event, receive) => {
    setMenuAnchor(event.currentTarget);
    setMenuReceive(receive);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuReceive(null);
  };

  const handleView = async (receive) => {
    try {
      setLoadingDetails(true);
      const res = await getReceiveById(receive._id);
      setSelectedReceive(res.data?.receive || receive);
      setViewDialogOpen(true);
    } catch (err) {
      console.error("Load receive details error:", err);
      enqueueSnackbar("❌ Failed to load receive details", { variant: "error" });
      setSelectedReceive(receive);
      setViewDialogOpen(true);
    } finally {
      setLoadingDetails(false);
      handleMenuClose();
    }
  };

  const handleExport = () => {
    // Simple CSV export
    const headers = ["Receive ID", "Date", "Vendor", "Dispatch", "Items Count", "Total Received", "Total Damaged", "Total Missing", "Received By"];
    const csvData = filteredReceives.map(receive => {
      const totals = getReceiveTotals(receive);
      return [
        receive._id?.slice(-8) || 'N/A',
        dayjs(receive.received_at || receive.createdAt).format('DD/MM/YYYY HH:mm'),
        receive.vendor?.name || 'Unknown Vendor',
        receive.dispatch?.dispatchNumber || 'N/A',
        receive.items?.length || 0,
        totals.received,
        totals.damaged,
        totals.missing,
        receive.createdBy?.name || receive.createdBy || 'System'
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `linen-receives-${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    enqueueSnackbar("📊 Data exported successfully", { variant: "success" });
  };

  const getReceiveTotals = (receive) => {
    if (!receive.items) return { received: 0, damaged: 0, missing: 0, weight: 0 };
    
    return receive.items.reduce(
      (acc, item) => {
        acc.received += Number(item.receivedQty || 0);
        acc.damaged += Number(item.damagedQty || 0);
        acc.missing += Number(item.missingQty || 0);
        acc.weight += Number(item.weight || 0);
        return acc;
      },
      { received: 0, damaged: 0, missing: 0, weight: 0 }
    );
  };

  const getSummaryStats = () => {
    const total = filteredReceives.length;
    const totalItems = filteredReceives.reduce((sum, receive) => sum + (receive.items?.length || 0), 0);
    
    const totals = filteredReceives.reduce(
      (acc, receive) => {
        const receiveTotals = getReceiveTotals(receive);
        acc.received += receiveTotals.received;
        acc.damaged += receiveTotals.damaged;
        acc.missing += receiveTotals.missing;
        return acc;
      },
      { received: 0, damaged: 0, missing: 0 }
    );

    const uniqueVendors = [...new Set(filteredReceives.map(receive => receive.vendor?._id).filter(Boolean))].length;

    return { total, totalItems, ...totals, uniqueVendors };
  };

  const getStatusColor = (dispatchStatus) => {
    const colors = {
      received: "success",
      partial_received: "warning",
      dispatched: "info"
    };
    return colors[dispatchStatus] || "default";
  };

  const currentStats = getSummaryStats();

  if (loading && receives.length === 0) {
    return (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: 400,
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
          Loading Receive History...
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
          Preparing your receive records
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
        {/* Enhanced Header */}
        <Card sx={{ 
          mb: 4, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: 3
        }}>
          <CardContent sx={{ p: 4 }}>
            <Grid container alignItems="center" spacing={3}>
              <Grid item>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Receipt sx={{ fontSize: 48, color: 'white' }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography 
                  variant="h2" 
                  fontWeight={800}
                  sx={{ 
                    color: 'white',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    mb: 1
                  }}
                >
                  Linen Receive History
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'white', 
                    opacity: 0.9,
                    fontSize: '1.3rem'
                  }}
                >
                  Track and manage all linen receive transactions
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<Add sx={{ fontSize: 28 }} />}
                  onClick={() => navigate("/hospital/housekeeping-and-linen-management/linen/linen-receive")}
                  sx={{ 
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    backgroundColor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  New Receive
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Enhanced Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="📊 Total Receives"
              value={currentStats.total}
              subtitle="Current period"
              color="primary"
              icon={<CheckCircle />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="✅ Items Received"
              value={currentStats.received}
              subtitle="Clean items"
              color="success"
              icon={<Inventory />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="🟡 Damaged Items"
              value={currentStats.damaged}
              subtitle="Require attention"
              color="warning"
              icon={<FilterList />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="🏢 Active Vendors"
              value={currentStats.uniqueVendors}
              subtitle="Participating vendors"
              color="info"
              icon={<Business />}
            />
          </Grid>
        </Grid>

        {/* Enhanced Filters Card */}
        <Card sx={{ 
          mb: 4, 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)'
        }}>
          <CardHeader
            title={
              <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                🔍 Filter & Search
              </Typography>
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50'
            }}
          />
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="🔍 Search by dispatch, vendor, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    sx: { fontSize: '1.1rem', py: 1.5 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 28, color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'white'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>📅 Period</InputLabel>
                  <Select
                    value={periodFilter}
                    label="📅 Period"
                    onChange={(e) => setPeriodFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem', borderRadius: 3 }}
                  >
                    {periodOptions.map(option => (
                      <MenuItem key={option.value} value={option.value} sx={{ fontSize: '1.1rem' }}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {periodFilter === "custom" && (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker
                      label="📅 From Date"
                      value={dateFrom}
                      onChange={(newValue) => setDateFrom(newValue)}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          sx: { 
                            '& .MuiInputBase-input': { fontSize: '1.1rem' },
                            borderRadius: 3
                          }
                        } 
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker
                      label="📅 To Date"
                      value={dateTo}
                      onChange={(newValue) => setDateTo(newValue)}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          sx: { 
                            '& .MuiInputBase-input': { fontSize: '1.1rem' },
                            borderRadius: 3
                          }
                        } 
                      }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>🏢 Vendor</InputLabel>
                  <Select
                    value={vendorFilter}
                    label="🏢 Vendor"
                    onChange={(e) => setVendorFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem', borderRadius: 3 }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '1.1rem' }}>All Vendors</MenuItem>
                    {vendors.map(vendor => (
                      <MenuItem key={vendor._id} value={vendor._id} sx={{ fontSize: '1.1rem' }}>
                        {vendor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Tooltip title="🔄 Refresh Data">
                    <IconButton 
                      onClick={loadData} 
                      size="large"
                      sx={{ 
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'primary.dark' },
                        width: 56,
                        height: 56
                      }}
                    >
                      <Refresh sx={{ fontSize: 28 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="📥 Export CSV">
                    <IconButton 
                      onClick={handleExport} 
                      size="large" 
                      disabled={filteredReceives.length === 0}
                      sx={{ 
                        backgroundColor: 'success.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'success.dark' },
                        width: 56,
                        height: 56,
                        '&:disabled': { backgroundColor: 'grey.400' }
                      }}
                    >
                      <CloudDownload sx={{ fontSize: 28 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Enhanced Receives Table Card */}
        <Card sx={{ 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          <CardHeader
            title={
              <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                📋 Receive Records
              </Typography>
            }
            action={
              <Stack direction="row" spacing={2} alignItems="center">
                {(searchTerm || vendorFilter !== "all") && (
                  <Chip
                    label="🎯 Filters Active"
                    color="primary"
                    size="large"
                    onDelete={() => {
                      setSearchTerm("");
                      setVendorFilter("all");
                      setPeriodFilter("month");
                    }}
                    sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                  />
                )}
                <Typography variant="h6" sx={{ fontSize: '1.3rem', fontWeight: 700, color: 'primary.main' }}>
                  {filteredReceives.length} Records
                </Typography>
              </Stack>
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50',
              py: 3
            }}
          />
          <CardContent sx={{ p: 0 }}>
            {filteredReceives.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Inventory sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                  {receives.length === 0 
                    ? "📭 No Receive Records Found" 
                    : "🔍 No Records Match Your Filters"}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary', mb: 3 }}>
                  {receives.length === 0 
                    ? "Create your first receive transaction to get started" 
                    : "Try adjusting your search criteria or filters"}
                </Typography>
                {receives.length === 0 && (
                  <Button
                    variant="contained"
                    startIcon={<Add sx={{ fontSize: 28 }} />}
                    onClick={() => navigate("/hospital/housekeeping-and-linen-management/linen/linen-receive")}
                    sx={{ 
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Create First Receive
                  </Button>
                )}
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: 'primary.main' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📅 Receive Date
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🏢 Vendor
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🚚 Dispatch
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📦 Items
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        ✅ Received
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🟡 Damaged
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🔴 Missing
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📊 Status
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        👤 Received By
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🛠️ Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReceives.map((receive) => {
                      const totals = getReceiveTotals(receive);
                      
                      return (
                        <TableRow 
                          key={receive._id} 
                          hover
                          sx={{ 
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              transform: 'translateY(-1px)',
                              transition: 'all 0.3s ease'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <TableCell>
                            <Stack>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {dayjs(receive.received_at || receive.createdAt).format('DD/MM/YYYY')}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                {dayjs(receive.received_at || receive.createdAt).format('HH:mm')}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {receive.vendor?.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                {receive.vendor?.vendorCode}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {receive.dispatch?.dispatchNumber || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={receive.items?.length || 0}
                              size="medium"
                              variant="outlined"
                              sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 700, color: "success.main" }}>
                              {totals.received}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ 
                              fontSize: '1.1rem', 
                              fontWeight: 700, 
                              color: totals.damaged > 0 ? "warning.main" : "text.secondary" 
                            }}>
                              {totals.damaged}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ 
                              fontSize: '1.1rem', 
                              fontWeight: 700, 
                              color: totals.missing > 0 ? "error.main" : "text.secondary" 
                            }}>
                              {totals.missing}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={receive.dispatch?.status || 'received'}
                              color={getStatusColor(receive.dispatch?.status)}
                              size="medium"
                              sx={{ fontSize: '1rem', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {receive.createdBy?.name || receive.createdBy || 'System'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="👁️ View Details">
                              <IconButton
                                size="large"
                                onClick={() => handleView(receive)}
                                sx={{ 
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '&:hover': { backgroundColor: 'primary.dark' }
                                }}
                              >
                                <Visibility sx={{ fontSize: 24 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Enhanced View Receive Dialog */}
        <Dialog 
          open={viewDialogOpen} 
          onClose={() => setViewDialogOpen(false)} 
          maxWidth="lg" 
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 3,
              boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: 'primary.main', 
            color: 'white',
            py: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Description sx={{ fontSize: 32 }} />
              <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
                Receive Details
              </Typography>
              {loadingDetails && <CircularProgress size={24} sx={{ color: 'white', ml: 2 }} />}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            {selectedReceive && (
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                    <CardHeader
                      title={
                        <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                          🏢 Vendor & Dispatch Information
                        </Typography>
                      }
                      sx={{ backgroundColor: 'grey.50' }}
                    />
                    <CardContent>
                      <Stack spacing={3}>
                        <Box>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                            🏭 Vendor
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                            {selectedReceive.vendor?.name}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
                            Code: {selectedReceive.vendor?.vendorCode}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                            🚚 Dispatch
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                            {selectedReceive.dispatch?.dispatchNumber || 'N/A'}
                          </Typography>
                          {selectedReceive.dispatch?.pickup_time && (
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
                              Pickup: {dayjs(selectedReceive.dispatch.pickup_time).format('DD/MM/YYYY HH:mm')}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                            📅 Receive Date & Time
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                            {dayjs(selectedReceive.received_at || selectedReceive.createdAt).format('DD/MM/YYYY HH:mm')}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                    <CardHeader
                      title={
                        <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                          📊 Receive Summary
                        </Typography>
                      }
                      sx={{ backgroundColor: 'grey.50' }}
                    />
                    <CardContent>
                      <Stack spacing={3}>
                        <Box>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                            📦 Total Items
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                            {selectedReceive.items?.length || 0}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                            👤 Received By
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                            {selectedReceive.createdBy?.name || selectedReceive.createdBy || 'System'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                            📊 Status
                          </Typography>
                          <Chip
                            label={selectedReceive.dispatch?.status || 'received'}
                            color={getStatusColor(selectedReceive.dispatch?.status)}
                            size="large"
                            sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                          />
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                <Grid item xs={12}>
                  <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                    <CardHeader
                      title={
                        <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                          📋 Item Details
                        </Typography>
                      }
                      sx={{ backgroundColor: 'grey.50' }}
                    />
                    <CardContent sx={{ p: 0 }}>
                      <TableContainer>
                        <Table>
                          <TableHead sx={{ backgroundColor: 'primary.main' }}>
                            <TableRow>
                              <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                📦 Item Name
                              </TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                🔤 Code
                              </TableCell>
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                📤 Sent Qty
                              </TableCell>
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                ✅ Received
                              </TableCell>
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                🟡 Damaged
                              </TableCell>
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                🔴 Missing
                              </TableCell>
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                ⚖️ Weight
                              </TableCell>
                              <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                                📝 Notes
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedReceive.items?.map((item, index) => (
                              <TableRow key={item._id || index} hover>
                                <TableCell>
                                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                    {item.linen?.name || 'Unknown Item'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                    {item.linen?.code || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                    {item.sentQty}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 700, color: "success.main" }}>
                                    {item.receivedQty}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body1" sx={{ 
                                    fontSize: '1.1rem', 
                                    fontWeight: 700, 
                                    color: item.damagedQty > 0 ? "warning.main" : "text.secondary" 
                                  }}>
                                    {item.damagedQty}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body1" sx={{ 
                                    fontSize: '1.1rem', 
                                    fontWeight: 700, 
                                    color: item.missingQty > 0 ? "error.main" : "text.secondary" 
                                  }}>
                                    {item.missingQty}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                    {item.weight || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1" sx={{ fontSize: '1.1rem', color: "text.secondary" }}>
                                    {item.notes || '-'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                            {selectedReceive.items && selectedReceive.items.length > 0 && (
                              <TableRow sx={{ backgroundColor: 'warning.light' }}>
                                <TableCell colSpan={2}>
                                  <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                                    📊 Totals
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                                    {selectedReceive.items.reduce((sum, item) => sum + Number(item.sentQty || 0), 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 800, color: "success.main" }}>
                                    {selectedReceive.items.reduce((sum, item) => sum + Number(item.receivedQty || 0), 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 800, color: "warning.main" }}>
                                    {selectedReceive.items.reduce((sum, item) => sum + Number(item.damagedQty || 0), 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 800, color: "error.main" }}>
                                    {selectedReceive.items.reduce((sum, item) => sum + Number(item.missingQty || 0), 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                                    {selectedReceive.items.reduce((sum, item) => sum + Number(item.weight || 0), 0).toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
                {selectedReceive.photos && selectedReceive.photos.length > 0 && (
                  <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                      <CardHeader
                        title={
                          <Typography variant="h4" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                            📸 Photos ({selectedReceive.photos.length})
                          </Typography>
                        }
                        sx={{ backgroundColor: 'grey.50' }}
                      />
                      <CardContent>
                        <Alert severity="info" sx={{ fontSize: '1.1rem', borderRadius: 2 }}>
                          📷 {selectedReceive.photos.length} photo(s) were uploaded with this receive.
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setViewDialogOpen(false)}
              sx={{ 
                fontSize: '1.1rem',
                px: 4,
                py: 1,
                borderRadius: 3
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }
          }}
        >
          <MenuItem onClick={() => handleView(menuReceive)} sx={{ fontSize: '1.1rem', py: 1.5 }}>
            <Visibility sx={{ mr: 2, fontSize: 24 }} /> View Details
          </MenuItem>
        </Menu>
      </Box>
    </LocalizationProvider>
  );
};